import { Concept } from './classes/Concepts';
import { COLORS } from './constants';
import { Doc } from './classes/Docs';
import { CategoricalField, Category } from './classes/MetadataFields';
import { ConceptManagementInformation } from './project_management/ConceptManagementInformation';
import { naturalSortBy } from './utils/NaturalSort';

export const makeConcept = (search, overrides) => {
  const parsed = Concept.fromString(search);
  const partial = { ...parsed, ...getTermIds(parsed), ...overrides };
  if (!parsed.boolean) {
    return Concept.fromJSON({ vectors: ['A'], ...partial });
  } else {
    const vectors = partial.exactTermIds.map(() => 'A');
    return Concept.fromJSON({ vectors, ...partial });
  }
};

export const makeSharedConcept = (search, overrides) => {
  const parsed = Concept.fromString(search);
  const partial = { ...parsed, ...overrides };
  return makeConcept(search, {
    sharedConceptId: `uuid-for-${partial.name}`,
    name: partial.name,
    color: COLORS[0],
    type: "or",
    ...overrides
  });
};

const getTermIds = parsed => {
  const trim = t => t.trim();
  const langTag = text => `${text.trim()}|en`;
  if (!parsed.boolean) {
    return { exactTermIds: parsed.texts.map(langTag) };
  } else {
    const trimmed = parsed.texts.map(trim);
    return {
      exactTermIds: trimmed.filter(text => text[0] !== '-').map(langTag),
      excludedTermIds: trimmed
        .filter(text => text[0] === '-')
        .map(text => langTag(text.slice(1)))
    };
  }
};

export function makeDoc(config = {}, options = {}) {
  return new Doc(docDefaults(config, options));
}

function docDefaults(
  { docId = 'default-doc-id', text, title, terms, fragments = [], ...other },
  { language = 'en' }
) {
  const defaults = {
    docId,
    text,
    terms: getTerms({ text, terms }, { language }),
    fragments,
    metadata: []
  };
  if (title) {
    defaults.title = title;
  }
  return { ...defaults, ...other };
}

// a dummy list
const ENGLISH_STOP_WORDS = [
  'a',
  'an',
  'the',
  'of',
  'to',
  'not',
  'and',
  'also',
  'I',
  'is',
  'are'
].map(w => new RegExp(`/${w}/i`));

function getTerms({ text, terms }, { language }) {
  if (terms) {
    return terms;
  }
  const words = text.split(/[\n ]/).map(w => w.replace(/[,.!?'"]/g, ''));
  const nonStopWords = [
    ...new Set(
      words.filter(word => ENGLISH_STOP_WORDS.every(sw => !sw.test(word)))
    )
  ];
  return nonStopWords.map(word => {
    const termId = `${word}|${language}`;
    const start = text.slice(0, text.indexOf(word)).length;
    const end = start + word.length;
    return { termId, start, end };
  });
}

export const makeCategoricalField = (name, categories) => {
  const values = categories.map(c => new Category(c));
  return new CategoricalField(name, values);
};

export const makeProject = (props = {}) => ({
  project_id: 'test-project',
  workspace_id: 'test-workspace',
  name: 'Project name',
  description: 'Project description',
  language: 'en',
  next_build_language: 'en',
  document_count: 100,
  last_successful_build_time: 1,
  last_build_info: {
    start_time: 0,
    stop_time: 1,
    success: true,
    science_version: '2.2.3',
    sentiment: { success: true }
  },
  creator: 'user',
  permissions: ['read', 'write', 'create'],
  ...props
});

export const makeMultipleProjects = n => {
  return [...Array(n)].map((_, i) =>
    makeProject({
      project_id: `id-${i}`,
      name: `Project ${i}`,
      document_count: i
    })
  );
};

export const makeVolumeView = (props = {}) => ({
  concepts: 'prevalent',
  doc_match_type: 'both',
  drivers_count: '50',
  drivers_of: 'Appearance score',
  feature: 'volume',
  match_type: 'total',
  sortby: 'matches-desc',
  prevalent_count: 50,
  ...props
});

export const makeDriversView = (props = {}) => ({
  concepts: 'drivers',
  doc_match_type: 'both',
  drivers_count: '50',
  drivers_of: 'Appearance score',
  feature: 'drivers',
  field: 'Appearance score',
  sortby: 'absoluteDifference-desc',
  tab: 'visualization',
  sync_fields: 'yes',
  ...props
});

export const makeConceptManagementWithIgnore = (
  numAdded,
  numDeleted,
  numUnchanged
) => {
  const addedConcepts = Array.from(
    { length: numAdded },
    (_, i) => 'added concept ' + (i + 1)
  );
  const deletedConcepts = Array.from(
    { length: numDeleted },
    (_, i) => 'deleted concept ' + (i + 1)
  );
  const unchangedConcepts = Array.from(
    { length: numUnchanged },
    (_, i) => 'unchanged concept ' + (i + 1)
  );

  const makeConcepts = concepts => concepts.map(c => ({ concept: c }));
  const currentIgnore = makeConcepts(unchangedConcepts.concat(deletedConcepts));
  const nextIgnore = makeConcepts(unchangedConcepts.concat(addedConcepts));

  const current_build = {
    ignore: currentIgnore,
    merge: [],
    notice: [],
    collocate: []
  };
  const next_build = {
    ignore: nextIgnore,
    merge: [],
    notice: [],
    collocate: []
  };

  const alphabetizeBuild = build => {
    for (const assertionType in build) {
      build[assertionType].sort(naturalSortBy('concept'));
    }
  };
  alphabetizeBuild(next_build);
  alphabetizeBuild(current_build);

  return new ConceptManagementInformation({ current_build, next_build });
};

export const makeConceptManagementDiffCounts = (
  numAdded,
  numDeleted,
  numUnchanged
) => {
  let concepts = [];
  concepts = concepts.concat(
    Array.from({ length: numAdded }, (_, i) => 'added concept ' + (i + 1)),
    Array.from({ length: numDeleted }, (_, i) => 'deleted concept ' + (i + 1)),
    Array.from(
      { length: numUnchanged },
      (_, i) => 'unchanged concept ' + (i + 1)
    )
  );

  const countMap = {};
  concepts.forEach((concept, index) => {
    // Only concepts that are not ignored in the current build have exact match
    // counts
    if (concept.startsWith('added')) {
      // set the fictional count of this concept to (5 + its index), which is
      // basically just an arbitrary way of assigning a number to each concept.
      countMap[concept] = 5 + index;
    } else {
      countMap[concept] = 0;
    }
  });

  return countMap;
};

export function makeUser(username, full_name = '', role = 'creator') {
  return { username, full_name, role };
}

export function makeWorkspace(
  name,
  workspace_id,
  permissions = ['read', 'write', 'create', 'account_manage'],
  organization_id = 'or123'
) {
  return { name, workspace_id, organization_id, permissions };
}

export function makeOrganization(
  organization_id,
  name,
  permissions = [
    'read',
    'write',
    'create',
    'account_manage',
    'organization_manage',
    'admin'
  ]
) {
  return { organization_id, name, permissions };
}

export const makeStatus = overrides => ({
  gpu: true,
  science_version: '2.0.0',
  minimum_science_version: '2.0.0',
  languages: [
    { code: 'en', name: 'English' },
    { code: 'ar', name: 'Arabic' },
    { code: 'id', name: 'Bahasa Indonesia' },
    { code: 'zh', name: 'Chinese' },
    { code: 'nl', name: 'Dutch' },
    { code: 'fr', name: 'French' },
    { code: 'de', name: 'German' },
    { code: 'it', name: 'Italian' },
    { code: 'ja', name: 'Japanese' },
    { code: 'ko', name: 'Korean' },
    { code: 'pl', name: 'Polish' },
    { code: 'pt', name: 'Portuguese' },
    { code: 'ru', name: 'Russian' },
    { code: 'es', name: 'Spanish' },
    { code: 'sv', name: 'Swedish' }
  ],
  ...overrides
});
